import { useVirtualizer, Virtualizer } from "@tanstack/react-virtual";
import { useMemo, MutableRefObject } from "react";

import { Entity, EntityType, getEntityNumericId } from "@/queries/folder-entities/types";

interface FlattenedItem {
  entity: Entity;
  depth: number;
  index: number;
  isCreatePlaceholder?: boolean;
  parentFolderId?: number;
}

interface UseVirtualizedTreeProps {
  rootItems: Entity[];
  expandedFolders: Set<number>;
  getChildren: (parentId: number | null) => Entity[];
  parentRef: MutableRefObject<HTMLDivElement | null>;
  estimateSize?: number;
  /** Offset of the list within the scroll container (e.g. Favorites above Home). */
  scrollMargin?: number;
}

interface UseVirtualizedTreeResult {
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  flattenedItems: FlattenedItem[];
}

export const useVirtualizedTree = ({
  rootItems,
  expandedFolders,
  getChildren,
  parentRef,
  estimateSize = 32,
  scrollMargin = 0,
}: UseVirtualizedTreeProps): UseVirtualizedTreeResult => {
  // Flatten the tree structure into a single array of visible items
  const flattenedItems = useMemo(() => {
    const items: FlattenedItem[] = [];
    let index = 0;

    const flattenRecursive = (entities: Entity[], depth: number) => {
      entities.forEach(entity => {
        items.push({
          entity,
          depth,
          index: (index += 1),
        });

        // If it's a folder and it's expanded, add its children
        const folderId =
          entity.type === EntityType.FOLDER ? getEntityNumericId(entity.id) : undefined;
        if (folderId !== undefined && expandedFolders.has(folderId)) {
          const children = getChildren(folderId);
          if (children.length > 0) {
            flattenRecursive(children, depth + 1);
          } else {
            // Empty expanded folder — inject a "New item" placeholder
            items.push({
              entity: {
                id: -folderId,
                name: "",
                type: EntityType.FOLDER,
                path: [],
              } as Entity,
              depth: depth + 1,
              index: (index += 1),
              isCreatePlaceholder: true,
              parentFolderId: folderId,
            });
          }
        }
      });
    };

    flattenRecursive(rootItems, 0);
    return items;
  }, [rootItems, expandedFolders, getChildren]);

  // Create the virtualizer - let it handle scroll naturally
  const virtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 5,
    scrollMargin,
    getItemKey: index => {
      const item = flattenedItems[index];
      if (!item) {
        return index;
      }
      return `${item.entity.type}-${item.entity.id}-${item.depth}`;
    },
  });

  return {
    virtualizer,
    flattenedItems,
  };
};
